import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Search from '@/components/filters/Search';
import CategoriesTable from './components/CategoriesTable';
import { fetchCategories } from '@/store/Category';
import AddNewGamicsButton from '@/components/buttons/AddNewGamicsButton';
import { CategoryType } from './types';
import AddCategory from './components/AddCategory';

const AdminGamics: React.FC = () => {
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryType | null>(
    null
  );

  const dispatchForC = useDispatch<any>();

  const loadCategories = async () => {
    try {
      const res = await dispatchForC(fetchCategories());
      const data = res.payload?.data || [];
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  const filteredCategories = categories.filter((category) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;

    return category.name.toLowerCase().includes(query);
  });

  const openAdd = () => {
    setEditingCategory(null);
    setIsModalOpen(true);
  };

  const openEdit = (idx: string) => {
    const categoryToEdit = categories.find((category) => category.id === idx);
    if (!categoryToEdit) return;
    setEditingCategory(categoryToEdit);
    setIsModalOpen(true);
  };

  const handleSuccess = async (title: string) => {
    if (editingCategory) {
      setCategories((prev) =>
        prev.map((category) =>
          category.id === editingCategory.id
            ? { ...category, name: title }
            : category
        )
      );
    } else {
      await loadCategories();
    }
  };

  /* ─── render ────────────────────────────────────────────────────────────── */
  return (
    <div className='flex h-full flex-col'>
      <div className='p-8 rounded-[24px] bg-white flex flex-col h-full min-h-0 space-y-[24px]'>
        {/* ─── header & filters ───────────────────────────────────────────── */}
        <div className='flex items-center gap-2 justify-between'>
          <div>
            <Search
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className='ml-auto w-[226px] inline-flex items-center'>
            <AddNewGamicsButton
              label='Add New Category'
              responsive={false}
              onClick={openAdd}
            />
          </div>
        </div>

        {/* ─── table ──────────────────────────────────────────────────────── */}
        <div className='flex-1 min-h-0'>
          <CategoriesTable data={filteredCategories} onEdit={openEdit} />
        </div>
      </div>
      {isModalOpen && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6'
          role='dialog'
          aria-modal='true'
          aria-label={editingCategory ? 'Edit category form' : 'New category form'}
          onClick={closeModal}
        >
          <div
            className='relative w-full max-w-[506px]'
            onClick={(event) => event.stopPropagation()}
          >
            <AddCategory
              mode={editingCategory ? 'edit' : 'add'}
              categoryId={editingCategory?.id}
              initialTitle={editingCategory?.name || ''}
              onClose={closeModal}
              onSuccess={handleSuccess}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGamics;
